import { type JSX, ReactElement, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuthSession } from "../hooks";
import { useHubUserExist } from "../../user/hooks";
import LoadingOverlay from "../../_app/components/LoadingOverlay";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useContextProvider } from "../../context/providers/ContextProvider";

interface PrivateRouteProps {
  contextNotRequired?: boolean;
  children: ReactElement<any, any> | null;
}

export const PrivateRoute = ({ contextNotRequired, children }: PrivateRouteProps): JSX.Element => {
  const navigate = useNavigate();
  const { authStatus } = useAuthenticator((context: any) => [context.authStatus]);
  const { data: session, isFetching: sessionFetching } = useAuthSession();
  const hasValidIdentityUser = session?.userSub;
  const { data: doesHubUserExist, isFetching: hubUserExistenceFetching } = useHubUserExist();
  const { isReady, isReadyForGroupSelection } = useContextProvider();

  // No Auth
  useEffect(() => {
    if (!sessionFetching && !hasValidIdentityUser) {
      navigate("/login");
    }
  }, [hasValidIdentityUser, sessionFetching, navigate]);

  // No Hub User
  useEffect(() => {
    if (hasValidIdentityUser && !hubUserExistenceFetching && !doesHubUserExist) {
      setTimeout(() => navigate("/no-user", { replace: true }));
    }
  }, [hasValidIdentityUser, hubUserExistenceFetching, doesHubUserExist, navigate]);

  // No Context
  useEffect(() => {
    if (!contextNotRequired && isReadyForGroupSelection) {
      navigate("/groups");
    }
  }, [isReadyForGroupSelection, contextNotRequired, navigate]);

  // The Amplify state machine starts as "configuring" on every page load — auth validity is
  // unknown yet, so wait instead of bouncing an already-signed-in user through /login.
  if (authStatus === "configuring" && hasValidIdentityUser) {
    return <LoadingOverlay />;
  }

  // Expired Auth
  if (authStatus !== "authenticated" && hasValidIdentityUser) {
    return <Navigate to="/login" />;
  }

  // Context Not Ready
  if (!isReady && !contextNotRequired) {
    return <LoadingOverlay />;
  }

  // Continue...
  if (doesHubUserExist) {
    return children || <div />;
  }

  // Fallback loader
  return <LoadingOverlay />;
};
